import { ArrowRightLeft, ChevronDown, FileSearch, Info, Loader2, TriangleAlert, XCircle, CheckCircle2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { StepRail, type Step } from '@/components/data/StepRail'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { ProgressBar } from '@/components/ProgressBar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ApiError,
  getTgeColumnMap,
  getTgeRun,
  getTgeRuns,
  runTgeAudit,
  saveTgeColumnMap,
  startTgeMigration,
  type TgeAuditResult,
  type TgeMigrateParams,
  type TgeRunDetail,
  type TgeRunSummary,
} from '@/lib/tge'
import { useSelection } from '@/lib/useSelection'

import {
  ColumnMapEditor,
  columnMapProblem,
  type ColumnOverrides,
  type MapSaveState,
} from './ColumnMapEditor'
import { TgeHistory } from './TgeHistory'
import { TgeParams, isDigits, type TgeForm } from './TgeParams'
import { RunProgress } from './TgeProgress'
import { AuditResult, BlockTitle, CollapsibleList, GRIDS, RunReport, gridLabel } from './TgeReports'

const POLL_INTERVAL_MS = 3000
// The run reports progress as it goes; 30 min without any change in it means it died.
const MAX_STALE_POLLS = 600

type NoticeKind = 'info' | 'success' | 'error'
type Notice = { kind: NoticeKind; text: string }

const NOTICE_STYLE: Record<NoticeKind, { icon: typeof Info; tone: string }> = {
  info: { icon: Info, tone: 'text-text-2' },
  success: { icon: CheckCircle2, tone: 'text-ok' },
  error: { icon: XCircle, tone: 'text-danger' },
}

function parseIssueKeys(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((k) => k.trim())
    .filter(Boolean)
}

const errText = (err: unknown) =>
  err instanceof ApiError ? `${err.detail} (HTTP ${err.status})` : (err as Error).message

export default function TgeMigrationPage() {
  const [form, setForm] = useState<TgeForm>({
    grid: GRIDS[0],
    // Any other TGE grid (Valgro, Budget, ...): grid, table and config ids are all explicit.
    custom: false,
    customGridId: '',
    customTableId: '',
    configId: '',
    issueKeysText: '',
    dryRun: true,
  })
  const set = (patch: Partial<TgeForm>) => setForm((f) => ({ ...f, ...patch }))
  const [paramsOpen, setParamsOpen] = useState(true)

  const [audit, setAudit] = useState<TgeAuditResult | null>(null)
  // grid|table|config the audit (and the column map built on it) belongs to
  const [auditScope, setAuditScope] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<ColumnOverrides>({})
  // the map saved for the grid → table in the form; every pick in the editor is saved
  const [mapSave, setMapSave] = useState<MapSaveState>({ status: 'loading' })
  const mapKeyRef = useRef<string | null>(null)
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve())
  const saveSeqRef = useRef(0)
  // "Map the columns" on a stopped run: the form takes the run's grid, then the audit runs
  const [auditPending, setAuditPending] = useState(false)
  const [auditing, setAuditing] = useState(false)
  const [elapsed, setElapsed] = useState(0)

  const [migrating, setMigrating] = useState(false)
  const [pollingId, setPollingId] = useState<number | null>(null)
  // Parameters frozen when the live-migration confirm opened: the dialog shows the scope
  // that will be sent, and the send uses exactly that, not a fresh read of the form.
  const [confirmParams, setConfirmParams] = useState<TgeMigrateParams | null>(null)
  const [run, setRun] = useState<TgeRunDetail | null>(null)
  // the run being polled, as last seen: its progress drives the bar
  const [liveRun, setLiveRun] = useState<TgeRunSummary | null>(null)
  const [runLoading, setRunLoading] = useState(false)
  // center view when both an audit and a run report exist
  const [view, setView] = useState<'run' | 'audit'>('run')
  const [runParam, setRunParam] = useSelection('run')

  const [runs, setRuns] = useState<TgeRunSummary[] | null>(null)
  // A failed history load used to become an empty list — "No migration runs yet." —
  // which hides a live run someone may need to reopen before starting another.
  const [runsError, setRunsError] = useState<string | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)

  const pollRef = useRef<number | null>(null)
  // Set synchronously for the length of the start request. `migrating` disables the
  // buttons only after a re-render, and a second click on "Migrate live" can land first.
  const startingRef = useRef(false)
  // run id being loaded/polled/shown: the ?run= effect must not reload it
  const requestedRunRef = useRef<number | null>(null)

  // Elapsed-seconds ticker: audit reads the whole grid from DC (paginated), which
  // can take minutes — the status line must visibly progress.
  useEffect(() => {
    if (!auditing) return
    setElapsed(0)
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => window.clearInterval(t)
  }, [auditing])

  const refreshRuns = () => {
    getTgeRuns(10)
      .then((r) => {
        setRuns(r)
        setRunsError(null)
      })
      .catch((err: Error) => setRunsError(errText(err)))
  }

  const stopPolling = () => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
    setPollingId(null)
  }

  useEffect(() => {
    refreshRuns()
    return stopPolling
  }, [])

  const showRun = (detail: TgeRunDetail) => {
    requestedRunRef.current = detail.id
    setRun(detail)
    setView('run')
    setRunParam(String(detail.id))
  }

  const finishPolling = (detail: TgeRunDetail) => {
    stopPolling()
    setMigrating(false)
    showRun(detail)
    refreshRuns()
    if (detail.status === 'ok') {
      setNotice({ kind: 'success', text: `Run #${detail.id} finished: ok.` })
    } else if (detail.report !== null && 'error' in detail.report) {
      // the whole run stopped: there is no per-issue report to point at
      setNotice({ kind: 'error', text: `Run #${detail.id} stopped: ${detail.report.error}` })
    } else {
      setNotice({
        kind: 'error',
        text: `Run #${detail.id} finished: ${detail.status} — see the per-issue report.`,
      })
    }
  }

  const startPolling = (runId: number) => {
    stopPolling()
    setPollingId(runId)
    setLiveRun(null)
    let stale = 0
    let lastProgress = ''
    const tick = () => {
      stale += 1
      if (stale > MAX_STALE_POLLS) {
        // The background job can die without finalizing the run row — don't spin forever.
        stopPolling()
        setMigrating(false)
        setNotice({
          kind: 'error',
          text: `Run #${runId} has not moved for ${Math.round((MAX_STALE_POLLS * POLL_INTERVAL_MS) / 60000)} min — check the backend logs or run history.`,
        })
        refreshRuns()
        return
      }
      getTgeRun(runId)
        .then((detail) => {
          if (detail.status !== 'running') {
            finishPolling(detail)
            return
          }
          const seen = JSON.stringify(detail.progress)
          if (seen !== lastProgress) {
            lastProgress = seen
            stale = 0
          }
          setLiveRun(detail)
        })
        .catch(() => {
          // transient fetch failure — keep polling
        })
    }
    tick() // immediate first check — dry runs over a few issues finish fast
    pollRef.current = window.setInterval(tick, POLL_INTERVAL_MS)
  }

  /** Text in the keys box that parses to no keys at all (e.g. only commas). Sent as
   * `issue_keys: null`, it would silently widen the run to the whole grid. */
  const keysUnparseable = form.issueKeysText.trim() !== '' && parseIssueKeys(form.issueKeysText).length === 0

  function migrateParams(): TgeMigrateParams {
    const keys = parseIssueKeys(form.issueKeysText)
    return {
      grid_id: form.custom ? Number(form.customGridId.trim()) : form.grid.grid,
      table_id: form.custom ? Number(form.customTableId.trim()) : null,
      config_id: form.configId.trim() === '' ? null : Number(form.configId.trim()),
      overrides: Object.keys(overrides).length > 0 ? overrides : null,
      issue_keys: keys.length > 0 ? keys : null,
      dry_run: form.dryRun,
    }
  }

  // An audit belongs to one grid → table → config: another target asks for a new one.
  const scope = form.custom
    ? `${form.customGridId.trim()}|${form.customTableId.trim()}|${form.configId.trim()}`
    : `${form.grid.grid}|${form.grid.table}|${form.configId.trim()}`
  const scopedAudit = audit !== null && auditScope === scope ? audit : null

  // The column map is saved per grid → table. It lived only in the page before, and runs
  // #20/#21 (2026-11-01) went out without one after the page was opened anew.
  const mapGrid = form.custom ? form.customGridId.trim() : String(form.grid.grid)
  const mapTable = form.custom ? form.customTableId.trim() : String(form.grid.table)
  const mapKey = isDigits(mapGrid) && isDigits(mapTable) ? `${mapGrid}|${mapTable}` : null
  useEffect(() => {
    mapKeyRef.current = mapKey
    setOverrides({})
    if (mapKey === null) {
      setMapSave({ status: 'saved', by: null, at: null })
      return
    }
    setMapSave({ status: 'loading' })
    const [g, t] = mapKey.split('|').map(Number)
    getTgeColumnMap(g, t)
      .then((m) => {
        if (mapKeyRef.current !== mapKey) return
        setOverrides(m.overrides)
        setMapSave({ status: 'saved', by: m.updated_by, at: m.updated_at })
      })
      .catch((e: unknown) => {
        if (mapKeyRef.current === mapKey) setMapSave({ status: 'error', op: 'load', error: errText(e) })
      })
  }, [mapKey])

  /** New map in the editor: shown at once, saved in order (each save carries the whole map). */
  function persistMap(next: ColumnOverrides) {
    setOverrides(next)
    if (mapKey === null) return
    const key = mapKey
    const [g, t] = key.split('|').map(Number)
    const seq = ++saveSeqRef.current
    const current = () => mapKeyRef.current === key && saveSeqRef.current === seq
    setMapSave({ status: 'saving' })
    saveChainRef.current = saveChainRef.current
      .then(() => saveTgeColumnMap(g, t, next))
      .then((m) => {
        if (current()) setMapSave({ status: 'saved', by: m.updated_by, at: m.updated_at })
      })
      .catch((e: unknown) => {
        if (current()) setMapSave({ status: 'error', op: 'save', error: errText(e) })
      })
  }

  /** The run stopped on an empty map: take its grid into the form and audit it. */
  function mapColumnsOf(r: TgeRunDetail) {
    const preset = GRIDS.find((g) => g.grid === r.grid_id && g.table === r.table_id)
    const cfg = r.params?.config_id
    setForm((f) => ({
      ...f,
      ...(preset
        ? { custom: false, grid: preset }
        : { custom: true, customGridId: String(r.grid_id), customTableId: String(r.table_id) }),
      configId: typeof cfg === 'number' ? String(cfg) : '',
    }))
    setParamsOpen(true)
    setAuditPending(true)
  }
  useEffect(() => {
    if (!auditPending) return
    setAuditPending(false)
    void onAudit()
  }, [auditPending]) // eslint-disable-line react-hooks/exhaustive-deps

  /** First problem with the parameters, checked before hitting the API. */
  function paramsError(): string | null {
    const v = form.configId.trim()
    if (form.custom && !isDigits(form.customGridId)) return 'TGE grid ID must be a number.'
    if (form.custom && !isDigits(form.customTableId)) return 'STAGIL Table ID must be a number.'
    if (form.custom && v === '') return 'A custom grid needs its STAGIL Field Configuration ID.'
    if (v !== '' && !isDigits(v)) return 'Config ID must be a number.'
    return null
  }

  async function onAudit() {
    const err = paramsError()
    if (err) {
      setNotice({ kind: 'error', text: err })
      return
    }
    setNotice(null)
    setAuditing(true)
    setView('audit')
    try {
      const { issue_keys: _keys, dry_run: _dry, ...params } = migrateParams()
      setAudit(await runTgeAudit(params))
      setAuditScope(scope)
    } catch (e) {
      setNotice({ kind: 'error', text: `Audit failed: ${errText(e)}` })
    } finally {
      setAuditing(false)
    }
  }

  async function onMigrate() {
    const err = paramsError()
    if (err) {
      setNotice({ kind: 'error', text: err })
      return
    }
    if (keysUnparseable) {
      setNotice({
        kind: 'error',
        text: 'The issue keys field has text but no keys — enter keys, or clear it to migrate the whole grid.',
      })
      return
    }
    const mapProblem = scopedAudit ? columnMapProblem(scopedAudit, overrides) : null
    if (mapProblem) {
      setNotice({ kind: 'error', text: mapProblem })
      setView('audit')
      return
    }
    const params = migrateParams()
    if (!params.dry_run) {
      setNotice(null) // a stale error must not sit above the confirm dialog
      setConfirmParams(params)
      return
    }
    await startMigration(params)
  }

  async function startMigration(params: TgeMigrateParams) {
    // One start at a time, and none while a run is being watched: a live migration
    // PUT-replaces STAGIL tables, so a double-submit must not become a second run.
    if (startingRef.current || pollRef.current !== null) return
    startingRef.current = true
    setNotice(null)
    setMigrating(true)
    setRun(null)
    setView('run')
    requestedRunRef.current = null
    setRunParam(null)
    try {
      const { run_id } = await startTgeMigration(params)
      requestedRunRef.current = run_id
      setNotice({
        kind: 'info',
        text: `${params.dry_run ? 'Dry run' : 'Live migration'} #${run_id} started — polling every 3s.`,
      })
      refreshRuns()
      startPolling(run_id)
    } catch (e) {
      // A 409 means some run holds the lock. If this page is polling one, that run is
      // still going — clearing `migrating` here would re-enable Migrate under it.
      if (pollRef.current === null) setMigrating(false)
      if (e instanceof ApiError && e.status === 409) {
        setNotice({ kind: 'info', text: 'A migration run is already in progress — open it from the run history.' })
        refreshRuns()
      } else {
        setNotice({ kind: 'error', text: `Migrate failed: ${errText(e)}` })
      }
    } finally {
      startingRef.current = false
    }
  }

  /** Show a finished run's report, or resume polling a running one. */
  async function openRun(id: number) {
    requestedRunRef.current = id
    setNotice(null)
    setRunLoading(true)
    setView('run')
    try {
      const detail = await getTgeRun(id)
      if (detail.status === 'running') {
        setRun(null)
        setMigrating(true)
        setRunParam(String(id))
        setNotice({ kind: 'info', text: `Run #${detail.id} is still running — polling every 3s.` })
        startPolling(detail.id)
        setLiveRun(detail)
      } else {
        showRun(detail)
      }
    } catch (e) {
      setNotice({ kind: 'error', text: `Failed to load run #${id}: ${(e as Error).message}` })
    } finally {
      setRunLoading(false)
    }
  }

  // ?run=<id>: a link (or Back) opens that run's report in place
  useEffect(() => {
    const id = runParam !== null && isDigits(runParam) ? Number(runParam) : null
    if (id === null || id === requestedRunRef.current) return
    if (startingRef.current || pollRef.current !== null) return
    void openRun(id)
  }, [runParam]) // eslint-disable-line react-hooks/exhaustive-deps

  const busy = auditing || migrating || runLoading
  // the polled run's row follows its progress; the list itself reloads only on start/finish
  const historyRuns = runs?.map((r) => (liveRun !== null && r.id === liveRun.id ? { ...r, progress: liveRun.progress } : r)) ?? null
  const confirmKeys = confirmParams?.issue_keys ?? null
  const confirmGrid = GRIDS.find((g) => g.grid === confirmParams?.grid_id)
    ?? (confirmParams?.table_id != null
      ? { grid: confirmParams.grid_id, table: confirmParams.table_id, label: '' }
      : form.grid)
  const noticeStyle = notice ? NOTICE_STYLE[notice.kind] : null
  const NoticeIcon = noticeStyle?.icon ?? Info

  const hasRun = migrating || runLoading || run !== null
  const hasAudit = auditing || audit !== null
  const centerView: 'run' | 'audit' | 'empty' = hasRun && hasAudit ? view : hasRun ? 'run' : hasAudit ? 'audit' : 'empty'

  const steps: Step[] = [
    { label: 'Audit', state: auditing ? 'current' : audit !== null || run !== null ? 'done' : 'current' },
    {
      label: 'Migrate',
      state: migrating ? 'current' : run !== null ? 'done' : audit !== null && !auditing ? 'current' : 'todo',
    },
    { label: 'Verify', state: run !== null && !migrating ? (run.status === 'ok' ? 'done' : 'current') : 'todo' },
  ]

  const target = form.custom
    ? `${form.customGridId.trim() || '?'} → ${form.customTableId.trim() || '?'}`
    : gridLabel(form.grid.grid, form.grid.table)
  const keyCount = parseIssueKeys(form.issueKeysText).length

  return (
    <Page
      title="TGE → STAGIL"
      hint="Push migration of TGE grid rows (Jira DC) into STAGIL Tables (Jira Cloud). Audit is read-only; migrate PUT-replaces each issue's table and verifies the result."
      context={(
        <>
          <StepRail steps={steps} />
          <button
            type="button"
            onClick={() => setParamsOpen((v) => !v)}
            aria-expanded={paramsOpen}
            aria-controls="tge-params"
            className="ml-2 inline-flex min-w-0 items-center gap-1.5 rounded-md border border-input bg-surface px-2.5 py-1 text-[12px] text-text-2 transition-colors hover:text-foreground"
          >
            <span className="truncate font-mono text-foreground">{target}</span>
            <span className="shrink-0">· {keyCount ? `${keyCount} keys` : 'whole grid'}</span>
            <span className="shrink-0">· config {form.configId.trim() || 'default'}</span>
            <span className={`shrink-0 ${form.dryRun ? '' : 'text-danger'}`}>· {form.dryRun ? 'dry run' : 'live'}</span>
            <ChevronDown className={`size-3.5 shrink-0 transition-transform ${paramsOpen ? 'rotate-180' : ''}`} aria-hidden />
            <span className="sr-only">{paramsOpen ? 'Hide parameters' : 'Edit parameters'}</span>
          </button>
        </>
      )}
      actions={(
        <>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void onAudit()}>
            {auditing
              ? <><Loader2 className="animate-spin" aria-hidden /> Auditing…</>
              : <><FileSearch aria-hidden /> Run audit</>}
          </Button>
          <Button size="sm" variant={form.dryRun ? 'default' : 'destructive'} disabled={busy} onClick={() => void onMigrate()}>
            {migrating
              ? <><Loader2 className="animate-spin" aria-hidden /> Migrating…</>
              : <><ArrowRightLeft aria-hidden /> Migrate{form.dryRun ? ' (dry run)' : ' (live)'}</>}
          </Button>
        </>
      )}
    >
      {paramsOpen && (
        <div id="tge-params">
          <TgeParams form={form} set={set} busy={busy} keysUnparseable={keysUnparseable} />
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* status line: errors live next to the work, not in a toast */}
          {(auditing || notice) && (
            <div className="shrink-0 border-b border-line px-4 py-1.5 text-[12px]" aria-live="polite">
              {auditing && (
                <span className="text-text-2">Reading the TGE grid from DC… can take minutes ({elapsed}s)</span>
              )}
              {!auditing && notice && noticeStyle && (
                <span
                  className={`flex items-center gap-1.5 ${noticeStyle.tone}`}
                  role={notice.kind === 'error' ? 'alert' : 'status'}
                >
                  <NoticeIcon className="size-3.5 shrink-0" aria-hidden />
                  {notice.text}
                </span>
              )}
            </div>
          )}

          {hasRun && hasAudit && (
            <div role="tablist" aria-label="Result" className="flex shrink-0 gap-4 border-b border-line px-4">
              {(['run', 'audit'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={view === v}
                  onClick={() => setView(v)}
                  className={`relative py-2 text-[12.5px] ${view === v ? 'text-foreground' : 'text-text-2 hover:text-foreground'}`}
                >
                  {v === 'run' ? 'Run report' : 'Audit'}
                  {view === v && <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />}
                </button>
              ))}
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {centerView === 'empty' && (
              <p className="max-w-prose px-4 py-4 text-[13px] text-text-2">
                No audit yet. Run the audit first — it reads the TGE grid, matches columns against the STAGIL
                fields config and predicts row counts, oversize issues and unresolved keys. Migrate defaults to a
                dry run; a live run PUT-replaces issue tables in STAGIL.
              </p>
            )}
            {centerView === 'audit' && (
              <SectionBoundary label="Audit" resetKeys={[audit, auditing]}>
                <div className="px-4 py-3">
                  {auditing
                    ? <ProgressBar indeterminate label="Auditing…" />
                    : audit !== null && (
                      <AuditResult audit={audit}>
                        {scopedAudit
                          ? (
                            <ColumnMapEditor
                              audit={scopedAudit}
                              overrides={overrides}
                              onChange={(col, target) => persistMap({ ...overrides, [col]: target })}
                              onReset={() => persistMap({})}
                              disabled={busy}
                              save={mapSave}
                              where={`grid ${mapGrid} → table ${mapTable}`}
                            />
                          )
                          : (
                            <p className="text-[12.5px] text-text-2">
                              The parameters changed since this audit — run the audit again to map columns.
                            </p>
                          )}
                      </AuditResult>
                    )}
                </div>
              </SectionBoundary>
            )}
            {centerView === 'run' && (
              <SectionBoundary label="Run report" resetKeys={[run]}>
                {migrating || runLoading
                  ? (
                    <div className="px-4 py-3">
                      {liveRun !== null && liveRun.id === pollingId
                        ? <RunProgress key={liveRun.id} run={liveRun} />
                        : (
                          <ProgressBar
                            indeterminate
                            label={pollingId !== null ? `Run #${pollingId} starting…` : 'Loading the run…'}
                          />
                        )}
                    </div>
                  )
                  : run !== null && (
                    <RunReport run={run} onMapColumns={busy ? undefined : () => mapColumnsOf(run)} />
                  )}
              </SectionBoundary>
            )}
          </div>
        </div>

        <aside aria-label="Run history" className="w-[300px] shrink-0 overflow-y-auto border-l border-line bg-pane px-3 py-3">
          <BlockTitle>Runs · last 10</BlockTitle>
          <SectionBoundary label="Run history" onRetry={refreshRuns} resetKeys={[runs]}>
            <TgeHistory
              runs={historyRuns}
              error={runsError}
              activeId={run?.id ?? pollingId}
              disabled={busy}
              onOpen={(id) => { setRunParam(String(id)); void openRun(id) }}
              onRetry={refreshRuns}
            />
          </SectionBoundary>
          {audit !== null && (
            <div className="mt-5">
              <BlockTitle>Audit warnings</BlockTitle>
              <SectionBoundary label="Audit warnings" resetKeys={[audit]}>
                <CollapsibleList title="Transform warnings" items={audit.warnings} defaultOpen />
              </SectionBoundary>
            </div>
          )}
        </aside>
      </div>

      {/* Live-migration confirm gate */}
      <Dialog
        open={confirmParams !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmParams(null)
        }}
      >
        <DialogContent className="max-w-md" showCloseButton={false}>
          <DialogTitle className="flex items-center gap-2 text-[13px] font-semibold text-danger">
            <TriangleAlert className="size-4" aria-hidden />
            Live migration
          </DialogTitle>
          <DialogDescription>
            This is not a dry run. Migrate PUT-replaces each issue's table in STAGIL (table {confirmGrid.table}) —
            existing rows there are overwritten irreversibly. Issues without TGE rows are skipped, never
            emptied.
          </DialogDescription>
          {/* The scope decides how much gets overwritten — state it, never leave it implied. */}
          <div className="rounded-md border border-line-strong bg-surface px-3 py-2.5">
            <div className="text-[11px] font-medium uppercase tracking-[0.07em] text-danger">Scope</div>
            <p className="mt-1 break-words font-mono text-[12px]">
              {confirmKeys
                ? (
                  <>
                    {confirmKeys.length} issue key{confirmKeys.length === 1 ? '' : 's'}:{' '}
                    {confirmKeys.slice(0, 10).join(', ')}
                    {confirmKeys.length > 10 ? ', …' : ''}
                  </>
                )
                : (
                  <>
                    <span className="font-semibold text-danger">The WHOLE grid</span>{' '}
                    {gridLabel(confirmGrid.grid, confirmGrid.table)} — no issue keys were given.
                  </>
                )}
            </p>
            {confirmParams?.overrides && (
              <p className="mt-1 font-mono text-[12px] text-text-2">
                column map: {Object.keys(confirmParams.overrides).length} column
                {Object.keys(confirmParams.overrides).length === 1 ? '' : 's'} set by hand
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmParams(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={migrating}
              onClick={() => {
                const params = confirmParams
                setConfirmParams(null)
                if (params) void startMigration(params)
              }}
            >
              <ArrowRightLeft aria-hidden /> Migrate live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
